/**
 * Dashboard transport boundary.
 *
 * The dashboard reads across domains, so it keeps its own thin transport
 * rather than reaching into four feature modules: every call here is a plain
 * read of a published collection or report, and each one is issued only when
 * the operator holds the permission its route demands.
 *
 * Nothing is aggregated client-side that the server did not already total.
 * Where the contract publishes no count — a keyset page has no total — the
 * screen says "20+" rather than inventing a number.
 */
import type { components } from "@erp/api-client";

import { apiFetchOperational as apiFetch, type Page } from "@/lib/api/http";

type Schemas = components["schemas"];

export type Money = Schemas["Money"];
export type ARAgingReport = Schemas["ARAgingReport"];
export type APAgingReport = Schemas["APAgingReport"];
export type PurchaseOrderSummary = Schemas["PurchasingPurchaseOrderSummary"];
export type ReorderSuggestion = Schemas["PurchasingReorderSuggestion"];
export type SalesOrderSummary = Schemas["SalesOrderSummary"];
export type FiscalPeriod = Schemas["FiscalPeriod"];

/** How many rows a "needs attention" tile asks for before it says "N+". */
export const ATTENTION_PAGE = 20;

/** How many rows the "latest activity" reads ask for. */
export const RECENT_PAGE = 5;

export function getARAging(currency: string, signal?: AbortSignal): Promise<ARAgingReport> {
  return apiFetch<ARAgingReport>("/reports/ar-aging", { query: { currency }, signal });
}

export function getAPAging(currency: string, signal?: AbortSignal): Promise<APAgingReport> {
  return apiFetch<APAgingReport>("/reports/ap-aging", { query: { currency }, signal });
}

export function listPurchaseOrdersAwaitingApproval(
  signal?: AbortSignal,
): Promise<Page<PurchaseOrderSummary>> {
  return apiFetch<Page<PurchaseOrderSummary>>("/purchase-orders", {
    query: { status: "pending_approval", limit: ATTENTION_PAGE },
    signal,
  });
}

export function listOpenReorderSuggestions(signal?: AbortSignal): Promise<Page<ReorderSuggestion>> {
  return apiFetch<Page<ReorderSuggestion>>("/reorder-suggestions", {
    query: { status: "open", limit: ATTENTION_PAGE },
    signal,
  });
}

export function listRecentSalesOrders(signal?: AbortSignal): Promise<Page<SalesOrderSummary>> {
  return apiFetch<Page<SalesOrderSummary>>("/sales-orders", {
    query: { limit: RECENT_PAGE },
    signal,
  });
}

export function listOpenFiscalPeriods(signal?: AbortSignal): Promise<Page<FiscalPeriod>> {
  return apiFetch<Page<FiscalPeriod>>("/fiscal-periods", {
    query: { status: "open", limit: RECENT_PAGE },
    signal,
  });
}
